// @flow
import { memo, useContext } from 'react'
import { useStore } from 'effector-react'
import cx from 'classnames'
import { Container } from 'atoms/container'
import { ContactPhone } from 'view/molecules/contact-phone'
import { UserService } from 'services/user'

import { getExternalInternalUrl } from 'utils/url'
import { isYandexTurboApp } from 'utils/browser'
import { Accordeon } from '../../atoms/accordeon'
import { Copyright } from '../../atoms/copyright'
import { LogoSk } from '../../atoms/logo-sk'
import { LicenseLink } from '../../atoms/license-link'
import { SocialLink } from '../../atoms/social-link'
import { MenuApp } from '../../molecules/app-menu'
import { Menu } from '../../molecules/footer-menu'
import { MenuContext } from '../../context'

import {
  FOXFORD_MENU_ITEMS,
  STUDY_MENU_ITEMS,
  STUDY_MENU_ITEMS_TEACHERS,
  SOCIAL_LINKS,
  SOCIAL_LINKS_FOR_TEACHERS,
} from '../../data/footerData'

import css from './Footer.sass'

type FooterProps = {|
  hidePhone?: boolean,
  forTeachers?: boolean,
|}

const foxfordMenuItems = FOXFORD_MENU_ITEMS.filter((item) =>
  process.env.REACT_APP_EXTERNAL === 'true' ? !item.hideOnExternal : true
)

const studyMenuItemsTeacher = STUDY_MENU_ITEMS_TEACHERS.filter((item) => {
  // eslint-disable-line
  return process.env.REACT_APP_EXTERNAL === 'true' ? !item.hideOnExternal : true
})

const studyMenuItems = STUDY_MENU_ITEMS.filter((item) =>
  process.env.REACT_APP_EXTERNAL === 'true' ? !item.hideOnExternal : true
)

/**
 * Task: [STOEGE-8973](https://jira.netology-group.ru/browse/STOEGE-8973)
 */
// eslint-disable-next-line prefer-arrow-callback
export const Footer = memo<FooterProps>(function Footer({ hidePhone = false, forTeachers = false }: FooterProps) {
  const user = useStore(UserService.getUserStore())
  const { options } = useContext(MenuContext)

  return (
    <div id='footerComponent' className={css.root}>
      <div className={css.main}>
        <Container className={css.container}>
          <div className={css.mainInner}>
            <div className={css.column}>
              <Accordeon heading='ФОКСФОРД'>
                <Menu items={foxfordMenuItems} urlParams={options.urlParams} />
              </Accordeon>
            </div>
            <div className={css.column}>
              <Accordeon heading='УЧИТЬСЯ В ФОКСФОРДЕ'>
                <Menu
                  items={forTeachers ? studyMenuItemsTeacher : studyMenuItems}
                  isAgent={user.type === 'agent'}
                  urlParams={options.urlParams}
                />
              </Accordeon>
            </div>
            <div className={css.column}>
              <Choose>
                <When condition={isYandexTurboApp()}>
                  <Accordeon heading='ДОПОЛНИТЕЛЬНО' className={css.hideXs}>
                    <LicenseLink
                      withText
                      withoutBorder
                      to={getExternalInternalUrl('/license', options.urlParams)}
                      className={cx(css.hideXs, css.licenceLink)}
                    />
                  </Accordeon>
                </When>
                <Otherwise>
                  <Accordeon heading='ПРИЛОЖЕНИЯ ФОКСФОРДА' className={css.hideXs}>
                    <MenuApp />
                    <LicenseLink
                      withText
                      to={getExternalInternalUrl('/license', options.urlParams)}
                      className={cx(css.hideXs, css.licenceLink)}
                    />
                  </Accordeon>
                  <MenuApp className={css.showXs} />
                </Otherwise>
              </Choose>
            </div>
          </div>
        </Container>
      </div>
      <div className={css.footer}>
        <Container className={css.container}>
          <div className={css.footerInner}>
            <div className={css.column}>
              <div className={css.social}>
                {/* eslint-disable-next-line max-len */}
                {(forTeachers ? SOCIAL_LINKS_FOR_TEACHERS : SOCIAL_LINKS).map((item, index) => (
                  <SocialLink {...item} key={item.id || index} />
                ))}
              </div>
            </div>
            <If condition={!hidePhone}>
              <div className={cx(css.column, css.hideSm, css.showXs)}>
                <Choose>
                  <When condition={process.env.REACT_APP_EXTERNAL === 'true'}>
                    <ContactPhone
                      isWhite
                      number={options.phone}
                      className={cx(css.phone, css.hideSm, css.showXs)}
                      size='l'
                    />
                  </When>
                  <Otherwise>
                    <ContactPhone isWhite className={cx(css.phone, css.hideSm, css.showXs)} size='l' />
                  </Otherwise>
                </Choose>
              </div>
            </If>
            <div className={cx(css.column, css.columnInfo)}>
              <div className={css.info}>
                <Copyright className={css.copy} />
                <If condition={!hidePhone}>
                  <Choose>
                    <When condition={process.env.REACT_APP_EXTERNAL === 'true'}>
                      <ContactPhone
                        isWhite
                        number={options.phone}
                        className={cx(css.phone, css.showSm, css.hideXs)}
                        size='s'
                      />
                    </When>
                    <Otherwise>
                      <ContactPhone isWhite className={cx(css.phone, css.showSm, css.hideXs)} size='s' />
                    </Otherwise>
                  </Choose>
                </If>
              </div>
              <LogoSk />
              <LicenseLink
                ariaLabel='страница лицензии'
                className={cx(css.showXs, css.licenceLink)}
                to={getExternalInternalUrl('/license', options.urlParams)}
              />
            </div>
          </div>
        </Container>
      </div>
    </div>
  )
})
